import React from "react";
import LegalPageLayout from "@/components/LegalPageLayout";
import { SITE } from "@/lib/site";

const sections = [
  {
    id: "agreement",
    title: "Your agreement",
    body: (
      <>
        <p>
          These Terms of Service ("Terms") govern your use of the fixlr.me website, the Fixlr platform, and any related services (together, the "Service") provided by Fixlr Ltd ("Fixlr", "we", "us"). By creating an account or using the Service, you agree to these Terms. If you don't agree, please don't use the Service.
        </p>
        <p>
          If you're agreeing on behalf of an organisation, you represent that you have authority to bind that organisation, and "you" refers to that organisation.
        </p>
      </>
    ),
  },
  {
    id: "accounts",
    title: "Accounts",
    body: (
      <>
        <p>You are responsible for the accuracy of the information you provide and for keeping your account credentials safe. You must:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Use the Service only for lawful purposes and in line with these Terms.</li>
          <li>Notify us promptly of any unauthorised access to your account.</li>
          <li>Not share login credentials between users — each user must have their own seat.</li>
        </ul>
        <p>We may suspend or terminate accounts that breach these Terms or that pose a security or compliance risk.</p>
      </>
    ),
  },
  {
    id: "subscriptions",
    title: "Subscriptions, trials and billing",
    body: (
      <>
        <p>
          Paid plans are provided on a subscription basis. The price, billing frequency and feature set for your plan are shown at sign-up and in your account settings.
        </p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Free trial</strong> — every paid plan includes a 7-day free trial, no credit card required. We will not auto-charge you when the trial ends; you must explicitly subscribe.</li>
          <li><strong>Renewals</strong> — paid subscriptions renew automatically at the end of each billing period unless cancelled before the renewal date.</li>
          <li><strong>Cancellation</strong> — you can cancel any time from your account settings. Cancellation takes effect at the end of the current billing period; we don't pro-rate part-month refunds except as required by law.</li>
          <li><strong>Taxes</strong> — prices exclude VAT and any other applicable taxes, which will be added at checkout.</li>
          <li><strong>One-time Licence</strong> — the Licence tier (£999 once) includes 12 months of updates and email support; renewal is optional thereafter.</li>
        </ul>
      </>
    ),
  },
  {
    id: "your-data",
    title: "Your data",
    body: (
      <>
        <p>
          Anything you upload to or generate inside the Service — properties, suppliers, jobs, photos, documents, chat messages — remains yours. You grant Fixlr a limited licence to process this data solely to provide and improve the Service for you.
        </p>
        <p>
          We act as a data processor for your data. Our handling of personal data is described in our <a href="/privacy" className="text-mk-lime font-semibold">Privacy Policy</a>. If you require a Data Processing Agreement (DPA), email us — we have a standard DPA we'll happily countersign.
        </p>
      </>
    ),
  },
  {
    id: "acceptable-use",
    title: "Acceptable use",
    body: (
      <>
        <p>You agree not to:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Use the Service to violate any law, regulation or third-party right.</li>
          <li>Upload malware, attempt to compromise security, or probe / scan for vulnerabilities without prior written consent.</li>
          <li>Reverse engineer the Service except to the extent expressly permitted by law.</li>
          <li>Resell or sublicense the Service except where you have a partner agreement with us.</li>
          <li>Use the Service to send unsolicited communications.</li>
        </ul>
      </>
    ),
  },
  {
    id: "ai-features",
    title: "AI features",
    body: (
      <>
        <p>
          Some features of the Service use generative AI. AI output can be inaccurate or incomplete; you remain responsible for reviewing and approving any output before relying on it for operational, legal or compliance decisions.
        </p>
        <p>
          We do not use your data to train external AI models. AI requests are sent to our model provider for the sole purpose of generating a reply for you, and are not retained for training.
        </p>
      </>
    ),
  },
  {
    id: "availability",
    title: "Availability and support",
    body: (
      <>
        <p>
          We aim for 99.9% monthly uptime measured at our application edge, excluding scheduled maintenance announced in advance. Email support is included on all paid plans, with priority support on Professional and dedicated CSM on Enterprise.
        </p>
      </>
    ),
  },
  {
    id: "warranties",
    title: "Warranties and disclaimers",
    body: (
      <>
        <p>
          We warrant that the Service will perform materially in line with the documentation we publish. To the maximum extent permitted by law, all other warranties are disclaimed and the Service is provided "as is".
        </p>
        <p>
          We do not warrant that AI-generated content is accurate or fit for any particular purpose. You are responsible for verifying it.
        </p>
      </>
    ),
  },
  {
    id: "liability",
    title: "Limitation of liability",
    body: (
      <>
        <p>
          To the maximum extent permitted by law, Fixlr's total liability for any claim arising out of or relating to the Service is limited to the fees you paid us in the 12 months immediately preceding the event giving rise to the claim. We are not liable for indirect or consequential losses, loss of profit, data, or business opportunity. Nothing in these Terms limits liability that cannot be limited by law (such as for death or personal injury caused by negligence).
        </p>
      </>
    ),
  },
  {
    id: "termination",
    title: "Termination",
    body: (
      <>
        <p>
          You may terminate at any time from your account settings. We may terminate or suspend your access if you materially breach these Terms (and don't fix it within 14 days of notice), or immediately for safety, security or legal reasons. On termination you can export your data for up to 30 days, after which we delete it from our active systems (backups follow our retention policy).
        </p>
      </>
    ),
  },
  {
    id: "law",
    title: "Governing law",
    body: (
      <>
        <p>
          These Terms are governed by the laws of England and Wales. The courts of England and Wales have exclusive jurisdiction, except that we may seek injunctive relief in any competent court.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes",
    body: (
      <>
        <p>
          We may update these Terms from time to time. Material changes will be notified by email or in-app notice at least 30 days before they take effect. Continuing to use the Service after a change means you accept the updated Terms.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <>
        <p>
          Questions about these Terms? Email <a href={`mailto:${SITE.email}`} className="text-mk-lime font-semibold">{SITE.email}</a>.
        </p>
      </>
    ),
  },
];

const Terms = () => (
  <LegalPageLayout
    slug="terms"
    eyebrow="Terms of Service"
    title="Fair, plain-English Terms of Service."
    lastUpdated="15 January 2026"
    metaTitle="Terms of Service"
    intro="The rules of the road for using Fixlr — short, fair and written in language you'll actually read."
    sections={sections}
  />
);

export default Terms;
